/**
 * AiActivity · AI 回答实时活动（全局单例）
 * ------------------------------------------------------------
 * 会话 store 经事件总线广播 chat:activity，本模块把它汇聚成
 * 「企管助手小人当前在干什么」：dock 小人与悬浮球据此念叨，
 * 让用户全站感知大模型处理阶段。阶段是服务端内存态瞬时 UI 信号，
 * 非业务事实；终态停留 4.5s 消失，活动态 100s 无事件兜底清场。
 */
import { computed, ref } from 'vue'
import { on } from '@/shared/events'
import type { ChatPhase } from '@/data/api/chat'

export type AiActivityStage = 'queued' | 'context' | 'think' | 'tool' | 'write' | 'ready' | 'failed' | 'cancelled' | 'interrupted'
export interface AiActivity { messageId: string; sessionId: string; stage: AiActivityStage; tool?: string; round?: number; at: number }

interface ChatActivityEvent { messageId?: string; sessionId?: string; status?: string; phase?: ChatPhase | null }

const current = ref<AiActivity | null>(null)

let doneTimer: ReturnType<typeof setTimeout> | undefined
let staleTimer: ReturnType<typeof setTimeout> | undefined

function clear() { current.value = null }
function armStale() {
  clearTimeout(staleTimer)
  /* 活动态超过 100s 无新事件：流已断，兜底清场避免小人永远「在忙」 */
  staleTimer = setTimeout(clear, 100000)
}

on('chat:activity', (e: ChatActivityEvent) => {
  if (!e?.messageId || !e?.sessionId) return
  clearTimeout(doneTimer)
  const status = e.status || ''
  if (status === 'ready' || status === 'failed' || status === 'cancelled' || status === 'interrupted') {
    clearTimeout(staleTimer)
    current.value = { messageId: e.messageId, sessionId: e.sessionId, stage: status, at: Date.now() }
    /* 终态念叨停留 4.5s 后自动消失 */
    doneTimer = setTimeout(clear, 4500)
    return
  }
  armStale()
  const phase = e.phase
  /* running 但 phase 尚未就位（认领瞬间）：同一条消息保持原阶段，新消息按排队处理 */
  const stage: AiActivityStage = phase ? phase.stage : (current.value?.messageId === e.messageId ? current.value.stage : 'queued')
  current.value = { messageId: e.messageId, sessionId: e.sessionId, stage, tool: phase?.tool, round: phase?.round, at: Date.now() }
})

/* 同一消息同一阶段稳定选同一条文案，避免阶段不变时文案乱跳 */
function pick(key: string, lines: string[]) {
  let h = 0
  for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) >>> 0
  return lines[h % lines.length]
}

const LINES: Record<string, string[]> = {
  queued: ['排队中，马上轮到我', '排个小队，马上到我'],
  context: ['翻翻之前的记录和材料', '先把前情和材料找齐'],
  think: ['正在想…', '琢磨一下…'],
  write: ['想好了，写回答中…', '动笔，把回答写出来…'],
  ready: ['写好了，点我去看看', '回答好了，点我看看'],
  failed: ['卡住了，点我去会话里重试'],
  cancelled: ['停下来了'],
  interrupted: ['被打断了，可去会话里重试'],
}
/* 工具名转人话：界面讲业务，不讲开发过程 */
const TOOL_LINES: Record<string, string[]> = {
  knowledge_search: ['去知识库搜一搜', '查一下知识库'],
  knowledge_get_version: ['翻开知识正文看看', '读一段知识正文'],
}
const TOOL_DEFAULT = ['用工具查查看', '调个工具帮帮忙']

const line = computed(() => {
  const a = current.value
  if (!a) return ''
  const key = a.messageId + ':' + a.stage
  if (a.stage === 'tool') return pick(key, TOOL_LINES[a.tool || ''] || TOOL_DEFAULT)
  if (a.stage === 'think' && (a.round ?? 0) > 0) return pick(key, [`看完资料，接着想（第 ${(a.round ?? 0) + 1} 轮）`, `再想一想（第 ${(a.round ?? 0) + 1} 轮）`])
  return pick(key, LINES[a.stage] || ['…'])
})

export function useAiActivity() { return { current, line } }
